import type { ExperimentConfig, ExperimentStep, ExperimentPart } from '@/types';

// ============================================================================
// EXPERIMENT 1: Qualitative Analysis of Household Compounds
// ============================================================================

// Color Codes for visual reference
export const householdCompoundColors = {
  // Solution Colors
  clearWater: '#E3F2FD',
  dissolved: '#BBDEFB',
  cloudySuspension: '#CFD8DC',
  iodineNegative: '#6D4C41',
  iodineStarch: '#1A237E',
  phenolphthaleinPink: '#E91E63',
  vinegar: '#FFF9C4',
  isopropylAlcohol: '#F5F5F5',
  // Precipitate Colors
  caco3Solid: '#FAFAFA',
  caso4Solid: '#F5F5F5',
  cornStarch: '#FFFDE7',
  mgOh2Precipitate: '#ECEFF1',
  // UI Colors
  workbench: '#795548',
  testTubeGlass: '#E3F2FD',
};

// Compound data for the experiment
export interface HouseholdCompound {
  id: string;
  name: string;
  formula: string;
  commonName: string;
  soluble: boolean;
  basicWithPhenolphthalein: boolean;
  carbonateReaction: boolean;
  iodinePositive: boolean;
  alcoholSoluble?: boolean;
  hotWaterSoluble?: boolean;
}

export const householdCompounds: Record<string, HouseholdCompound> = {
  na2co3: {
    id: 'na2co3',
    name: 'Sodium Carbonate',
    formula: 'Na2CO3',
    commonName: 'Washing Soda',
    soluble: true,
    basicWithPhenolphthalein: true,
    carbonateReaction: true,
    iodinePositive: false,
  },
  nahco3: {
    id: 'nahco3',
    name: 'Sodium Bicarbonate',
    formula: 'NaHCO3',
    commonName: 'Baking Soda',
    soluble: true,
    basicWithPhenolphthalein: false,
    carbonateReaction: true,
    iodinePositive: false,
  },
  nacl: {
    id: 'nacl',
    name: 'Sodium Chloride',
    formula: 'NaCl',
    commonName: 'Table Salt',
    soluble: true,
    basicWithPhenolphthalein: false,
    carbonateReaction: false,
    iodinePositive: false,
    alcoholSoluble: false,
    hotWaterSoluble: false,
  },
  caco3: {
    id: 'caco3',
    name: 'Calcium Carbonate',
    formula: 'CaCO3',
    commonName: 'Chalk',
    soluble: false,
    basicWithPhenolphthalein: false,
    carbonateReaction: true,
    iodinePositive: false,
  },
  mgso4: {
    id: 'mgso4',
    name: 'Magnesium Sulfate',
    formula: 'MgSO4',
    commonName: 'Epsom Salt',
    soluble: true,
    basicWithPhenolphthalein: false,
    carbonateReaction: false,
    iodinePositive: false,
  },
  sucrose: {
    id: 'sucrose',
    name: 'Sucrose',
    formula: 'C12H22O11',
    commonName: 'Table Sugar',
    soluble: true,
    basicWithPhenolphthalein: false,
    carbonateReaction: false,
    iodinePositive: false,
    alcoholSoluble: false,
    hotWaterSoluble: true,
  },
  caso4: {
    id: 'caso4',
    name: 'Calcium Sulfate',
    formula: 'CaSO4',
    commonName: 'Drying Agent',
    soluble: false,
    basicWithPhenolphthalein: false,
    carbonateReaction: false,
    iodinePositive: false,
  },
  h3bo3: {
    id: 'h3bo3',
    name: 'Boric Acid',
    formula: 'H3BO3',
    commonName: 'Eyewash',
    soluble: true,
    basicWithPhenolphthalein: false,
    carbonateReaction: false,
    iodinePositive: false,
    alcoholSoluble: true,
  },
  naoh: {
    id: 'naoh',
    name: 'Sodium Hydroxide',
    formula: 'NaOH',
    commonName: 'Drano',
    soluble: true,
    basicWithPhenolphthalein: true,
    carbonateReaction: false,
    iodinePositive: false,
  },
  cornstarch: {
    id: 'cornstarch',
    name: 'Corn Starch',
    formula: '(C6H10O5)n',
    commonName: 'Cornstarch',
    soluble: false,
    basicWithPhenolphthalein: false,
    carbonateReaction: false,
    iodinePositive: true,
  },
};

// Part 1: Setup and Labeling
const part1Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Safety Check',
    shortDescription: 'Review safety procedures for handling household chemicals',
    handler: 'handleSafetyAcknowledgement',
    infoContent: {
      title: 'Lab Safety',
      content: `Before beginning, review these safety guidelines:
- Wear safety goggles at all times
- Some compounds (NaOH, acids) are caustic - avoid skin contact
- Work in a well-ventilated area
- Do not taste or ingest any chemicals
- Wash hands thoroughly after the experiment
- Disposal considerations: Isopropyl alcohol, NaOH (see appendix G)`,
      whatHappens: 'Safety protocols are reviewed before handling any chemicals to prevent accidents and ensure proper lab conduct.',
      observe: [
        'Confirm safety goggles are worn and fit properly',
        'Locate the nearest eyewash station and fire extinguisher',
        'Identify which compounds require special handling (NaOH, acids)',
      ],
    },
  },
  {
    id: 2,
    title: 'Label Test Tubes',
    shortDescription: 'Label 10 test tubes for each compound',
    handler: 'handleLabelTestTubes',
    validation: {
      requires: ['safetyAcknowledged'],
    },
    infoContent: {
      title: 'Proper Labeling',
      content: `Label each test tube with the compound name or number. Proper labeling is essential in qualitative analysis to avoid confusion and ensure accurate results. You will test: Na2CO3, NaHCO3, NaCl, CaCO3, MgSO4, Sucrose, CaSO4, H3BO3, NaOH, and Corn Starch.`,
      whatHappens: 'Ten test tubes are labeled with compound identifiers to organize the systematic testing of household chemicals.',
      observe: [
        'Each test tube has a clear, readable label',
        'Labels correspond to the ten compounds being tested',
        'Test tubes are arranged in order in the rack for easy reference',
      ],
    },
  },
  {
    id: 3,
    title: 'Add Compounds to Tubes',
    shortDescription: 'Add a small amount of each compound to its labeled tube',
    handler: 'handleAddCompounds',
    validation: {
      requires: ['tubesLabeled'],
    },
    infoContent: {
      title: 'Sample Preparation',
      content: `Using the scoopula, add a pea-sized amount of each compound to its corresponding test tube. Use a clean scoopula for each compound to avoid contamination.`,
      whatHappens: 'A small quantity of each white solid compound is transferred into its labeled test tube using a clean scoopula to prevent cross-contamination.',
      observe: [
        'All ten compounds appear as white solids of varying texture',
        'Pea-sized amounts are consistent across all tubes',
        'The scoopula is cleaned between each compound transfer',
      ],
    },
  },
];

// Part 2: Solubility Test
const part2Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Add Water',
    shortDescription: 'Add 5 mL deionized water to each test tube',
    handler: 'handleAddWater',
    infoContent: {
      title: 'Solubility Testing',
      content: `Solubility is the ability of a substance to dissolve in a solvent. We use deionized water to test solubility. Compounds that dissolve will form clear solutions, while insoluble compounds will form precipitates or suspensions.`,
      whatHappens: 'Deionized water is added to each test tube to determine whether the compound dissolves (forms a clear solution) or remains as a precipitate or suspension.',
      observe: [
        'Note which tubes produce clear, transparent solutions (soluble)',
        'Note which tubes show undissolved solids settling at the bottom (insoluble)',
        'Corn starch forms a distinctive cloudy suspension rather than settling',
      ],
    },
  },
  {
    id: 2,
    title: 'Mix Solutions',
    shortDescription: 'Shake or stir each tube to mix',
    handler: 'handleMixTubes',
    validation: {
      requires: ['waterAdded'],
    },
    infoContent: {
      title: 'Mixing for Dissolution',
      content: `Agitation helps break up the solid and increases the rate of dissolution. Observe each tube carefully after mixing to determine solubility.`,
      whatHappens: 'Swirling or stirring each tube breaks up solid particles and increases the rate of dissolution by exposing fresh surfaces to the solvent.',
      observe: [
        'Soluble compounds dissolve fully upon agitation, leaving a clear solution',
        'Insoluble compounds remain as visible particles even after thorough mixing',
        'Note any heat released or absorbed during dissolution',
      ],
    },
  },
  {
    id: 3,
    title: 'Record Solubility',
    shortDescription: 'Record which compounds dissolved and which did not',
    handler: 'handleRecordSolubility',
    validation: {
      requires: ['tubesMixed'],
    },
    infoContent: {
      title: 'Expected Results',
      content: `Soluble compounds (clear solutions): Na2CO3, NaHCO3, NaCl, MgSO4, Sucrose, H3BO3, NaOH

Insoluble compounds (precipitates/suspensions): CaCO3, CaSO4, Corn Starch

Note: Corn starch forms a cloudy suspension rather than settling completely.`,
      whatHappens: 'The solubility results divide the ten compounds into two groups: seven soluble compounds that form clear aqueous solutions, and three insoluble compounds that remain as solids or suspensions.',
      observe: [
        'Seven tubes should contain clear solutions (Na2CO3, NaHCO3, NaCl, MgSO4, Sucrose, H3BO3, NaOH)',
        'CaCO3 and CaSO4 settle as white solids at the bottom of their tubes',
        'Corn starch forms a milky, opaque suspension that does not fully settle',
      ],
    },
  },
];

// Part 3: Iodine Test (for insoluble compounds)
const part3Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Identify Insoluble Compounds',
    shortDescription: 'Set aside the 3 insoluble compound tubes',
    handler: 'handleSelectInsoluble',
    infoContent: {
      title: 'Starch Detection',
      content: `The iodine test is specific for starch. We will test the three insoluble compounds (CaCO3, CaSO4, Corn Starch) to identify which one is starch.`,
      whatHappens: 'The three insoluble compounds (CaCO3, CaSO4, and Corn Starch) are separated for specific iodine testing to identify the starch among them.',
      observe: [
        'Isolate the three test tubes containing insoluble compounds',
        'All three appear as white solids or suspensions before testing',
        'Note the different textures: powdery CaCO3, granular CaSO4, and fine Corn Starch',
      ],
    },
  },
  {
    id: 2,
    title: 'Add Iodine',
    shortDescription: 'Add 1-2 drops of iodine tincture to each insoluble compound',
    handler: 'handleAddIodine',
    validation: {
      requires: ['insolubleSelected'],
    },
    infoContent: {
      title: 'The Starch-Iodine Complex',
      content: `Add **1-2 drops** of tincture of iodine to each insoluble compound and wash it down the sides with a little water. When iodine comes into contact with starch, it forms a deep blue-black complex (the typical starch-I₂ complex). This occurs because iodine molecules (I₂) insert themselves into the helical structure of amylose (a component of starch), creating a charge-transfer complex that absorbs light differently.`,
      chemicalEquation: 'Starch + I2 -> Starch-I2 (blue-black complex)',
      whatHappens: 'Iodine molecules insert into the helical amylose structure of starch, forming a deep blue-black charge-transfer complex, while non-starch compounds remain brownish-purple.',
      observe: [
        'Corn starch turns a dramatic deep blue-black color (positive starch test)',
        'CaCO3 and CaSO4 show only a brownish-purple iodine color (negative)',
        'The color change with starch is immediate and striking compared to the other tubes',
      ],
    },
  },
  {
    id: 3,
    title: 'Record Iodine Results',
    shortDescription: 'Record which compound turned blue-black',
    handler: 'handleRecordIodineResults',
    validation: {
      requires: ['iodineAdded'],
    },
    infoContent: {
      title: 'Results Interpretation',
      content: `- CaCO3 and CaSO4: Brownish-purple color (negative)
- Corn Starch: Deep blue-black color (POSITIVE)

The corn starch is now identified. We need additional tests to distinguish CaCO3 from CaSO4.`,
      whatHappens: 'The iodine test positively identifies corn starch by its blue-black color, leaving CaCO3 and CaSO4 still needing differentiation through additional testing.',
      observe: [
        'Corn starch tube displays a deep blue-black color (positive for starch)',
        'CaCO3 and CaSO4 tubes show only brownish-purple iodine color (negative)',
        'Record which compound is starch and note two unknowns remain',
      ],
    },
  },
];

// Part 4: Vinegar Test (Carbonate Detection)
const part4Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Prepare Fresh Samples',
    shortDescription: 'Add fresh CaCO3 and CaSO4 to new test tubes',
    handler: 'handlePrepareCarbonateTest',
    infoContent: {
      title: 'Carbonate Detection',
      content: `If the iodine test is negative (a brownish purple iodine colour appears), place a repeat small amount of the unknown solids in clean test tubes. Add about **1 mL of vinegar**, and note whether gas bubbles are produced. The evolution of carbon dioxide gas positively identifies calcium carbonate (CaCO₃). Otherwise the solid must be calcium sulphate (CaSO₄).`,
      whatHappens: 'Fresh samples of the two remaining insoluble compounds are placed in clean dry test tubes for the vinegar (acetic acid) carbonate detection test.',
      observe: [
        'Use fresh, clean dry test tubes for each compound',
        'Both solids appear as fine white powders before acid addition',
        'Ensure approximately equal amounts of each compound are used',
      ],
    },
  },
  {
    id: 2,
    title: 'Add Vinegar',
    shortDescription: 'Add vinegar (acetic acid) to both tubes',
    handler: 'handleAddVinegar',
    validation: {
      requires: ['carbonateTestPrepared'],
    },
    infoContent: {
      title: 'Carbonate-Acid Reaction',
      content: `When carbonate ions react with acids, they produce carbon dioxide gas, water, and a salt. The bubbling you observe is CO2 escaping from the solution.`,
      chemicalEquation: 'CaCO3 + 2CH3COOH -> Ca(CH3COO)2 + H2O + CO2(g)',
      whatHappens: 'Acetic acid in vinegar reacts with carbonate ions to produce carbon dioxide gas bubbles, water, and a dissolved calcium acetate salt.',
      observe: [
        'CaCO3 produces vigorous effervescence (bubbling) when vinegar is added',
        'CaSO4 shows no reaction or gas evolution with vinegar',
        'The bubbles are CO2 gas escaping from the solution',
      ],
    },
  },
  {
    id: 3,
    title: 'Record Carbonate Results',
    shortDescription: 'Record which compound produced bubbles',
    handler: 'handleRecordCarbonateResults',
    validation: {
      requires: ['vinegarAdded'],
    },
    infoContent: {
      title: 'Results',
      content: `- CaCO3: Vigorous bubbling (POSITIVE for carbonate)
- CaSO4: No bubbling (negative)

CaCO3 is now distinguished from CaSO4.`,
      whatHappens: 'The vinegar test distinguishes CaCO3 (vigorous bubbling from CO2 evolution) from CaSO4 (no reaction), completing identification of all three insoluble compounds.',
      observe: [
        'CaCO3 tube shows vigorous bubbling (positive for carbonate)',
        'CaSO4 tube remains unchanged with no gas evolution (negative)',
        'All three insoluble compounds are now identified: CaCO3, CaSO4, and Corn Starch',
      ],
    },
  },
];

// Part 5: Phenolphthalein Test (Soluble Compounds)
const part5Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Select Soluble Solutions',
    shortDescription: 'Prepare the 7 soluble compound solutions',
    handler: 'handleSelectSoluble',
    infoContent: {
      title: 'pH Indicator Test',
      content: `Phenolphthalein is a pH indicator that is colorless in acidic and neutral solutions but turns vivid pink/magenta in basic solutions (pH > 8.2).`,
      whatHappens: 'The seven soluble compound solutions are gathered for phenolphthalein indicator testing to identify which produce basic (alkaline) solutions.',
      observe: [
        'All seven soluble solutions appear clear and colorless before indicator addition',
        'Arrange the seven tubes in order for systematic testing',
        'Have phenolphthalein indicator solution ready with an eye dropper',
      ],
    },
  },
  {
    id: 2,
    title: 'Add Phenolphthalein',
    shortDescription: 'Add 1-2 drops of phenolphthalein to each soluble solution',
    handler: 'handleAddPhenolphthalein',
    validation: {
      requires: ['solubleSelected'],
    },
    infoContent: {
      title: 'Phenolphthalein as pH Indicator',
      content: `Add **1-2 drops of phenolphthalein** to the seven water soluble solutions. Two of them — sodium hydroxide (NaOH) and sodium carbonate (Na₂CO₃) — produce alkaline solutions basic enough to give a vivid pink colour. Further testing is required to differentiate between the two.`,
      whatHappens: 'Phenolphthalein indicator is added to each soluble solution; it turns vivid pink in strongly basic solutions (pH > 8.2) and remains colorless in neutral or acidic solutions.',
      observe: [
        'Na2CO3 and NaOH solutions turn vivid pink/magenta (strongly basic)',
        'Five remaining solutions stay colorless (neutral or weakly acidic/basic)',
        'NaHCO3 remains colorless despite being mildly basic (pH ~8.3 is near the threshold)',
      ],
    },
  },
  {
    id: 3,
    title: 'Record Phenolphthalein Results',
    shortDescription: 'Record which solutions turned pink',
    handler: 'handleRecordPhenolphthaleinResults',
    validation: {
      requires: ['phenolphthaleinAdded'],
    },
    infoContent: {
      title: 'Results',
      content: `Pink/Magenta (BASIC):
- Na2CO3: Vivid pink (pH ~11.5)
- NaOH: Vivid pink (pH ~14)

Colorless (Neutral/Acidic):
- NaHCO3: Colorless (pH ~8.3 - not basic enough)
- NaCl: Colorless (neutral)
- MgSO4: Colorless (neutral)
- Sucrose: Colorless (neutral)
- H3BO3: Colorless (weakly acidic)`,
      whatHappens: 'The phenolphthalein test divides the seven soluble compounds into two basic solutions (Na2CO3 and NaOH) and five neutral/acidic solutions requiring further differentiation.',
      observe: [
        'Two tubes show vivid pink color: Na2CO3 (pH ~11.5) and NaOH (pH ~14)',
        'Five tubes remain colorless: NaHCO3, NaCl, MgSO4, Sucrose, H3BO3',
        'The pink color intensity may differ slightly between Na2CO3 and NaOH',
      ],
    },
  },
];

// Part 6: Differentiation Tests
const part6Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Distinguish Na2CO3 from NaOH',
    shortDescription: 'Use vinegar test to differentiate basic compounds',
    handler: 'handleBasicDifferentiation',
    infoContent: {
      title: 'Distinguishing Basic Compounds',
      content: `Both Na₂CO₃ and NaOH turned pink with phenolphthalein. To determine which is the sodium carbonate, put a fresh sample of each into two clean dry test tubes, add vinegar, and look for the evolution of carbon dioxide bubbles. Bubbling confirms the presence of sodium carbonate.`,
      chemicalEquation: 'Na2CO3 + 2CH3COOH -> 2NaCH3COO + H2O + CO2(g)',
      whatHappens: 'Fresh samples of the two basic compounds are treated with vinegar; sodium carbonate produces CO2 bubbles while NaOH does not, allowing differentiation.',
      observe: [
        'Na2CO3 produces visible bubbling when vinegar is added (CO2 evolution)',
        'NaOH shows no gas evolution with vinegar (it simply neutralizes the acid)',
        'The compound that bubbles is confirmed as Na2CO3; the other is NaOH',
      ],
    },
  },
  {
    id: 2,
    title: 'Identify MgSO4',
    shortDescription: 'Place small amount in test tube with 5 mL H2O, add 2 drops aqueous NaOH',
    handler: 'handleMgSO4Test',
    infoContent: {
      title: 'Magnesium Hydroxide Precipitation',
      content: `Add **2 drops of aqueous NaOH** to the MgSO₄ solution. Magnesium hydroxide precipitates as a white gelatinous solid (insoluble hydroxide) because Mg(OH)₂ is insoluble in water.

**pH Testing Technique:** Dip a stirring rod into the solution and touch the rod to a piece of litmus paper placed on a **watch glass** — do not dip litmus directly into the solution.`,
      chemicalEquation: 'MgSO4(aq) + 2NaOH(aq) -> Mg(OH)2(s) + Na2SO4(aq)',
      whatHappens: 'Adding NaOH to MgSO4 solution causes insoluble magnesium hydroxide to precipitate as a white gelatinous solid, confirming the presence of Mg2+ ions.',
      observe: [
        'A white gelatinous precipitate of Mg(OH)2 forms immediately upon NaOH addition',
        'The precipitate is distinctly different from a clear solution',
        'Test pH with litmus paper on a watch glass using the stirring rod technique',
      ],
    },
  },
  {
    id: 3,
    title: 'Identify NaHCO3',
    shortDescription: 'Use vinegar test on remaining neutral compounds',
    handler: 'handleNaHCO3Test',
    infoContent: {
      title: 'Bicarbonate Test',
      content: `Place a small amount of each of the four remaining compounds (NaHCO3, NaCl, Sucrose, H3BO3) in separate clean dry test tubes. Add **2 mL of vinegar** to each. Only NaHCO3 will produce CO₂ bubbles because it contains bicarbonate.`,
      chemicalEquation: 'NaHCO3 + CH3COOH -> NaCH3COO + H2O + CO2(g)',
      whatHappens: 'Vinegar is added to the four remaining neutral compounds; only sodium bicarbonate reacts with acid to produce CO2 gas bubbles.',
      observe: [
        'NaHCO3 fizzes and bubbles when vinegar is added (positive for bicarbonate)',
        'NaCl, Sucrose, and H3BO3 show no gas evolution (negative)',
        'The bubbling from NaHCO3 is less vigorous than Na2CO3 but still clearly visible',
      ],
    },
  },
  {
    id: 4,
    title: 'Identify H3BO3',
    shortDescription: 'Use isopropyl alcohol solubility test',
    handler: 'handleAlcoholTest',
    infoContent: {
      title: 'Boric Acid Solubility in Alcohol',
      content: `Add **7-8 mL of isopropyl alcohol** (70% rubbing alcohol) to test tubes containing NaCl, Sucrose, and H₃BO₃. Boric acid is more soluble in alcohols than other ionic compounds because it has weak covalent bonding characteristics and can form hydrogen bonds with alcohol molecules. Ionic compounds like NaCl cannot dissolve in non-polar solvents.`,
      whatHappens: 'Isopropyl alcohol is added to the three remaining compounds; boric acid dissolves due to its covalent character and hydrogen bonding ability, while NaCl and sucrose do not.',
      observe: [
        'H3BO3 dissolves in the isopropyl alcohol, forming a clear solution',
        'NaCl remains as undissolved white solid at the bottom',
        'Sucrose also remains undissolved in the alcohol',
      ],
    },
  },
  {
    id: 5,
    title: 'Distinguish NaCl from Sucrose',
    shortDescription: 'Use hot water solubility test',
    handler: 'handleHotWaterTest',
    infoContent: {
      title: 'Temperature and Solubility',
      content: `Add the white solid to the **1 mL level** in the test tube, then add **5 mL water** (any 5 to 1 ratio is acceptable). Mix and heat both test tubes under the **hot water tap for 3 minutes**. Heating under hot tap water causes the sucrose to dissolve completely. Only some of the sodium chloride will dissolve as the water is heated — the rest will remain as a solid at the bottom of the test tube.`,
      whatHappens: 'Heating the two remaining compounds in water exploits the large temperature-dependent solubility of sucrose; it dissolves completely while NaCl solubility barely changes with temperature.',
      observe: [
        'Sucrose dissolves completely in hot water after 3 minutes, leaving a clear solution',
        'NaCl only partially dissolves, with visible solid remaining at the bottom',
        'The difference in solubility response to temperature identifies each compound',
        'Sucrose has a much steeper solubility curve with temperature than NaCl',
      ],
    },
  },
];

// Part 7: Unknown Identification
const part7Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Receive Unknown',
    shortDescription: 'Obtain your unknown compound sample',
    handler: 'handleReceiveUnknown',
    infoContent: {
      title: 'Unknown Identification Challenge',
      content: `You will receive one of the 10 household compounds as an unknown. Using the systematic approach you learned, perform the necessary tests to identify it. Follow the flow chart logic: solubility -> specific tests based on solubility result.`,
      whatHappens: 'An unknown white solid compound is provided for identification using the systematic flow chart approach developed throughout the experiment.',
      observe: [
        'Note the appearance and texture of the unknown solid',
        'Plan your testing sequence: start with solubility, then branch accordingly',
        'Compare unknown behavior to the known results from earlier parts',
      ],
    },
  },
  {
    id: 2,
    title: 'Test Unknown Solubility',
    shortDescription: 'Determine if your unknown is soluble in water',
    handler: 'handleTestUnknownSolubility',
    validation: {
      requires: ['unknownReceived'],
    },
  },
  {
    id: 3,
    title: 'Perform Confirmation Tests',
    shortDescription: 'Based on solubility, perform appropriate tests',
    handler: 'handleUnknownConfirmationTests',
    validation: {
      requires: ['unknownSolubilityTested'],
    },
  },
  {
    id: 4,
    title: 'Submit Identification',
    shortDescription: 'Submit your final identification of the unknown',
    handler: 'handleSubmitIdentification',
    validation: {
      requires: ['confirmationTestsComplete'],
    },
  },
];

// Initial States
const part1InitialState = {
  safetyAcknowledged: false,
  tubesLabeled: false,
  compoundsAdded: false,
  testTubes: {} as Record<string, { labeled: boolean; compound: string | null; contents: string[] }>,
};

const part2InitialState = {
  waterAdded: false,
  tubesMixed: false,
  solubilityResults: {} as Record<string, boolean>,
};

const part3InitialState = {
  insolubleSelected: false,
  iodineAdded: false,
  iodineResults: {} as Record<string, 'positive' | 'negative'>,
};

const part4InitialState = {
  carbonateTestPrepared: false,
  vinegarAdded: false,
  carbonateResults: {} as Record<string, boolean>,
};

const part5InitialState = {
  solubleSelected: false,
  phenolphthaleinAdded: false,
  phenolphthaleinResults: {} as Record<string, 'pink' | 'colorless'>,
};

const part6InitialState = {
  basicDifferentiationComplete: false,
  mgso4Identified: false,
  nahco3Identified: false,
  h3bo3Identified: false,
  naclVsSucroseComplete: false,
};

const part7InitialState = {
  unknownReceived: false,
  unknownId: null as string | null,
  unknownSolubilityTested: false,
  confirmationTestsComplete: false,
  submittedIdentification: null as string | null,
  isCorrect: null as boolean | null,
};

// Part Definitions
const part1: ExperimentPart = {
  id: 1,
  name: 'setup',
  title: 'Part I: Setup & Labeling',
  steps: part1Steps,
  equipment: ['Test tube rack', '10 test tubes', 'Scoopula', 'Labels', 'Marker'],
  initialState: part1InitialState,
};

const part2: ExperimentPart = {
  id: 2,
  name: 'solubility',
  title: 'Part II: Solubility Test',
  steps: part2Steps,
  equipment: ['Deionized water dispenser', 'Stirring rod'],
  initialState: part2InitialState,
};

const part3: ExperimentPart = {
  id: 3,
  name: 'iodine',
  title: 'Part III: Iodine Test',
  steps: part3Steps,
  equipment: ['Tincture of iodine', 'Eye dropper', 'Water bottle'],
  initialState: part3InitialState,
};

const part4: ExperimentPart = {
  id: 4,
  name: 'vinegar',
  title: 'Part IV: Vinegar Test',
  steps: part4Steps,
  equipment: ['Vinegar', 'Fresh clean dry test tubes'],
  initialState: part4InitialState,
};

const part5: ExperimentPart = {
  id: 5,
  name: 'phenolphthalein',
  title: 'Part V: Phenolphthalein Test',
  steps: part5Steps,
  equipment: ['Phenolphthalein indicator', 'pH indicator chart'],
  initialState: part5InitialState,
};

const part6: ExperimentPart = {
  id: 6,
  name: 'differentiation',
  title: 'Part VI: Differentiation Tests',
  steps: part6Steps,
  equipment: ['Aqueous NaOH solution', 'Vinegar', 'Isopropyl alcohol', 'Hot water tap'],
  initialState: part6InitialState,
};

const part7: ExperimentPart = {
  id: 7,
  name: 'unknown',
  title: 'Part VII: Unknown Identification',
  steps: part7Steps,
  equipment: ['All reagents from previous parts', 'Flow chart reference'],
  initialState: part7InitialState,
};

// ============================================================================
// FULL EXPERIMENT CONFIGURATION
// ============================================================================

const experiment1Config: ExperimentConfig = {
  id: 'exp1',
  number: 1,
  name: 'Qualitative Analysis of Household Compounds',
  description: 'Identify ten white solid household compounds through systematic physical and chemical testing including solubility, pH indicators, and specific confirmation tests.',
  estimatedTime: '90 minutes',
  learningObjectives: [
    'Apply systematic qualitative analysis techniques to identify unknown substances',
    'Understand solubility differences between ionic and covalent compounds',
    'Use chemical indicators (phenolphthalein, iodine) for identification',
    'Recognize carbonate reactions with acids (CO2 evolution)',
    'Distinguish between similar compounds using specific chemical tests',
    'Develop flow chart logic for systematic identification',
  ],
  safetyNotes: [
    'Wear safety goggles at all times',
    'NaOH is caustic - avoid skin contact',
    'Clean all glassware and label all glassware',
    'Use only the quantity of reagent required - avoid pouring large quantities which go unused',
    'Always use deionized water',
    'Mix solutions thoroughly by swirling - do NOT cover the top of the test tube with your hand and shake',
    'To determine pH, dip a stirring rod into the solution and touch the rod to a piece of litmus paper placed on a watch glass',
    'Do not taste or ingest any chemicals',
    'Wash hands thoroughly after the experiment',
  ],
  requiredEquipment: [
    'Test tube rack',
    '10+ test tubes',
    'Scoopula',
    'Labelling tape and pen',
    'Graduated cylinder',
    'Beaker',
    'Water bottle',
    'Plastic funnel',
    'Eye dropper',
    'Watch glass (for litmus paper testing)',
    'Stirring rod',
    'Deionized water (from deioniser apparatus)',
    'Tincture of iodine',
    'Vinegar',
    'Phenolphthalein indicator solution',
    'Aqueous NaOH solution (2 drops per test)',
    'Isopropyl alcohol (rubbing alcohol, 7-8 mL)',
    'Hot water tap',
    'Red and blue litmus paper',
  ],
  parts: [part1, part2, part3, part4, part5, part6, part7],
  quiz: {
    experimentId: 'exp1',
    title: 'Household Compounds Quiz',
    questions: [
      {
        id: 'exp1-q1',
        type: 'mcq' as const,
        question: 'Which reagent is used to detect the presence of starch in a household compound?',
        options: [
          { id: 'a', text: 'Phenolphthalein indicator' },
          { id: 'b', text: 'Tincture of iodine' },
          { id: 'c', text: 'Vinegar (acetic acid)' },
          { id: 'd', text: 'Isopropyl alcohol' },
        ],
        correctOptionId: 'b',
        explanation: 'Tincture of iodine turns dark blue-black in the presence of starch due to the formation of a starch-iodine complex. This is a specific and sensitive test for starch.',
      },
      {
        id: 'exp1-q2',
        type: 'mcq' as const,
        question: 'A white solid dissolves in water and the solution turns pink when phenolphthalein is added. It also fizzes with vinegar. The compound is most likely:',
        options: [
          { id: 'a', text: 'Sodium chloride (NaCl)' },
          { id: 'b', text: 'Calcium sulfate (CaSO₄)' },
          { id: 'c', text: 'Sodium carbonate (Na₂CO₃)' },
          { id: 'd', text: 'Corn starch' },
        ],
        correctOptionId: 'c',
        explanation: 'Sodium carbonate is soluble, basic (turns phenolphthalein pink), and reacts with acids to produce CO₂ gas (fizzing). This combination of properties uniquely identifies Na₂CO₃.',
      },
      {
        id: 'exp1-q3',
        type: 'mcq' as const,
        question: 'What does a positive carbonate test (fizzing with vinegar) indicate about the compound?',
        options: [
          { id: 'a', text: 'The compound is an acid' },
          { id: 'b', text: 'The compound contains the CO₃\u00B2⁻ ion which reacts with acid to release CO₂ gas' },
          { id: 'c', text: 'The compound is insoluble in water' },
          { id: 'd', text: 'The compound contains starch' },
        ],
        correctOptionId: 'b',
        explanation: 'Carbonates react with acids according to: CO₃\u00B2⁻ + 2H⁺ → H₂O + CO₂\u2191. The visible fizzing is caused by CO₂ gas being released.',
      },
      {
        id: 'exp1-q4',
        type: 'short_answer' as const,
        question: 'Explain why phenolphthalein turns pink in a solution of sodium carbonate but remains colorless in a solution of sodium chloride.',
        sampleAnswer: 'Sodium carbonate is a basic salt. When dissolved in water, the carbonate ion hydrolyzes: CO₃\u00B2⁻ + H₂O → HCO₃⁻ + OH⁻, producing hydroxide ions that raise the pH above 8.2. Phenolphthalein changes from colorless to pink above pH 8.2. Sodium chloride is a neutral salt that does not hydrolyze, so the pH remains around 7 and phenolphthalein stays colorless.',
      },
      {
        id: 'exp1-q5',
        type: 'calculation' as const,
        question: 'If you dissolve 0.500 g of an unknown compound in 10.0 mL of water and the compound is identified as sodium carbonate (Na₂CO₃, molar mass = 105.99 g/mol), how many moles of Na₂CO₃ are present?',
        expectedValue: 0.00472,
        tolerance: 2,
        unit: 'mol',
      },
    ],
  },
};

export default experiment1Config;
